import { useMemo, useRef, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Loader2, Ban, CheckCircle2, Upload, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  tenantsApi,
  type TenantSummary,
  type CreateTenantBody,
  type CreateTenantResult,
} from '@/api/tenants.api';

export const Route = createFileRoute('/_authenticated/operadores')({
  beforeLoad: ({ context }) => {
    if (!context.auth.user?.isSuperAdmin) {
      throw redirect({ to: '/chats' });
    }
  },
  component: OperadoresPage,
});

function slugify(value: string): string {
  const stripped = value
    .toLowerCase()
    .normalize('NFD')
    .split('')
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code < 0x300 || code > 0x36f;
    })
    .join('');
  return stripped.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const formSchema = z.object({
  brandName: z.string().min(1, 'Requerido').max(120),
  slug: z.string().min(2, 'Mínimo 2 caracteres').max(40).regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones'),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Color hex inválido').optional().or(z.literal('')),
  welcomeText: z.string().max(300).optional().or(z.literal('')),
  adminUsername: z.string().min(3, 'Mínimo 3 caracteres').max(32).regex(/^[a-zA-Z0-9_.-]+$/, 'Sin espacios ni símbolos raros'),
  adminPassword: z.string().min(6, 'Mínimo 6 caracteres').max(128),
});

type FormValues = z.infer<typeof formSchema>;

function OperadoresPage() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [createdCreds, setCreatedCreds] = useState<(CreateTenantResult & { adminPassword: string }) | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateTenantBody) => tenantsApi.create(body),
    onError: (err: Error) => toast.error(err.message || 'Error al crear el operador'),
  });

  const uploadLogoMutation = useMutation({
    mutationFn: ({ tenantId, file }: { tenantId: string; file: File }) => tenantsApi.uploadLogo(tenantId, file),
    onError: () => toast.error('El operador se creó, pero falló la subida del logo'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) =>
      tenantsApi.update(id, { status }),
    onSuccess: () => {
      toast.success('Estado actualizado');
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: () => toast.error('Error al actualizar el estado'),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      brandName: '',
      slug: '',
      primaryColor: '#6366f1',
      welcomeText: '',
      adminUsername: '',
      adminPassword: '',
    },
  });

  const onBrandNameChange = (value: string) => {
    if (!slugTouched) {
      form.setValue('slug', slugify(value));
    }
  };

  const onSubmit = async (values: FormValues) => {
    const result = await createMutation.mutateAsync({
      slug: values.slug,
      brandName: values.brandName,
      primaryColor: values.primaryColor || undefined,
      welcomeText: values.welcomeText || undefined,
      adminUsername: values.adminUsername,
      adminPassword: values.adminPassword,
    });

    if (logoFile) {
      await uploadLogoMutation.mutateAsync({ tenantId: result.tenantId, file: logoFile });
    }

    qc.invalidateQueries({ queryKey: ['tenants'] });
    toast.success('Operador creado');
    setIsOpen(false);
    setCreatedCreds({ ...result, adminPassword: values.adminPassword });
    form.reset();
    setLogoFile(null);
    setSlugTouched(false);
  };

  const handleCopyCreds = async () => {
    if (!createdCreds) return;
    const text = `Usuario: ${createdCreds.adminUsername}\nContraseña: ${createdCreds.adminPassword}\nLink: ${createdCreds.inviteBaseUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('¡Copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const columns = useMemo<ColumnDef<TenantSummary>[]>(
    () => [
      {
        accessorKey: 'brandName',
        header: 'Marca',
        cell: ({ row }) => <div className="font-medium">{row.original.brandName || '—'}</div>,
      },
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.slug}</span>,
      },
      {
        accessorKey: 'operators',
        header: 'Cajeros',
        cell: ({ row }) => row.original.operators,
      },
      {
        accessorKey: 'clients',
        header: 'Jugadores',
        cell: ({ row }) => row.original.clients,
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={row.original.status === 'active' ? 'default' : 'secondary'}>
            {row.original.status === 'active' ? 'Activo' : 'Suspendido'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const t = row.original;
          const isActive = t.status === 'active';
          return (
            <Button
              size="sm"
              variant="outline"
              className={isActive ? 'text-red-500' : 'text-green-600'}
              disabled={updateMutation.isPending}
              onClick={() => updateMutation.mutate({ id: t.id, status: isActive ? 'suspended' : 'active' })}
            >
              {isActive ? <Ban className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              {isActive ? 'Suspender' : 'Activar'}
            </Button>
          );
        },
      },
    ],
    [updateMutation],
  );

  const isSaving = createMutation.isPending || uploadLogoMutation.isPending;

  return (
    <>
      <TopBanner
        title="Operadores"
        subtitle="Alta de operadores (tenants) y branding"
        action={
          <Button
            className="h-11 shrink-0 lg:h-10"
            onClick={() => {
              form.reset({
                brandName: '',
                slug: '',
                primaryColor: '#6366f1',
                welcomeText: '',
                adminUsername: '',
                adminPassword: '',
              });
              setLogoFile(null);
              setSlugTouched(false);
              setIsOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo operador
          </Button>
        }
      />
      <div className="flex-1 overflow-auto overflow-x-hidden p-4 lg:p-6">
        <DataTable
          columns={columns}
          data={tenants}
          isLoading={isLoading}
          searchPlaceholder="Buscar operador..."
          searchColumn="brandName"
        />
      </div>

      {/* Crear operador */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo operador</DialogTitle>
            <DialogDescription>Da de alta un tenant con su branding y usuario admin</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="brandName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre de marca</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          onBrandNameChange(e.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          setSlugTouched(true);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="primaryColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color principal</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <input
                        type="color"
                        value={field.value || '#6366f1'}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-10 w-10 rounded border p-1"
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="welcomeText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Texto de bienvenida (opcional)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <FormLabel>Logo (opcional)</FormLabel>
                <div className="mt-1 flex items-center gap-2">
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="mr-2 h-4 w-4" />
                    {logoFile ? logoFile.name : 'Elegir archivo'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                  />
                </div>
              </div>
              <FormField
                control={form.control}
                name="adminUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuario admin</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="adminPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contraseña admin</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit" disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Crear operador
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Credenciales generadas */}
      <Dialog open={!!createdCreds} onOpenChange={() => setCreatedCreds(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Operador creado</DialogTitle>
            <DialogDescription>Guardá estas credenciales, no se vuelven a mostrar</DialogDescription>
          </DialogHeader>
          {createdCreds && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-4 font-mono text-sm">
              <div>Usuario: {createdCreds.adminUsername}</div>
              <div>Contraseña: {createdCreds.adminPassword}</div>
              <div className="break-all">Link base: {createdCreds.inviteBaseUrl}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => void handleCopyCreds()}>
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
              {copied ? '¡Copiado!' : 'Copiar'}
            </Button>
            <Button onClick={() => setCreatedCreds(null)}>Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
